import { useAuth } from "react-oidc-context";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from "react-router";
import { useApi } from "./api";
import { signOutUrl, type PortalConfig } from "./config";
import { HeaderSearch } from "./HeaderSearch";
import { PrintSlips } from "./pages/PrintSlips";
import { PrintInvoices } from "./pages/PrintInvoices";
import { FollowUpsPage } from "./pages/FollowUpsPage";
import { TicketPage } from "./pages/TicketPage";
import { TicketsPage } from "./pages/TicketsPage";
import type { Lookups } from "./types";

export function App({ config }: { config: PortalConfig }) {
  const auth = useAuth();

  if (auth.isLoading) return <Centered config={config}><p>Loading…</p></Centered>;

  if (auth.error) {
    return (
      <Centered config={config}>
        <p className="error">Sign-in problem: {auth.error.message}</p>
        <button onClick={() => void auth.signinRedirect()}>Try again</button>
      </Centered>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Centered config={config}>
        <p>Staff only. Sign in with your portal account.</p>
        <button
          className="primary"
          // Come back to the page that was asked for (e.g. a ticket link) after signing in.
          onClick={() => void auth.signinRedirect({ state: window.location.pathname + window.location.search })}
        >
          Sign in
        </button>
      </Centered>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/print/invoices" element={<PrintInvoices config={config} />} />
        <Route path="/print/checkin/:ticketNo" element={<PrintSlips config={config} />} />
        <Route path="*" element={<Portal config={config} />} />
      </Routes>
    </BrowserRouter>
  );
}

function Portal({ config }: { config: PortalConfig }) {
  const auth = useAuth();
  const lookups = useApi<Lookups>(config, "/lookups");
  const location = useLocation();
  const email = auth.user?.profile.email ?? "";

  return (
    <div className="app">
      <header className="topbar">
        <Link to="/tickets" className="brand">Watch Repair Pros</Link>
        <nav>
          <Link to="/tickets" className={location.pathname.startsWith("/tickets") ? "active" : ""}>Tickets</Link>
          <Link to="/follow-ups" className={location.pathname.startsWith("/follow-ups") ? "active" : ""}>Follow-ups</Link>
        </nav>
        <HeaderSearch config={config} />
        <div className="who">
          <span className="muted" title={`Version ${config.version}`}>{email}</span>
          <button
            onClick={() => {
              void auth.removeUser().then(() => {
                window.location.href = signOutUrl(config, window.location.origin);
              });
            }}
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="content">
        {lookups.error && <p className="error">Could not load statuses: {lookups.error.message}</p>}
        <Routes>
          <Route path="/" element={<Navigate to="/tickets" replace />} />
          <Route path="/tickets" element={<TicketsPage config={config} lookups={lookups.data} />} />
          <Route path="/tickets/:ticketNo" element={<TicketPage config={config} lookups={lookups.data} />} />
          <Route path="/follow-ups" element={<FollowUpsPage config={config} />} />
          <Route path="*" element={<p>Page not found. <Link to="/tickets">Go to tickets</Link></p>} />
        </Routes>
      </main>
    </div>
  );
}

function Centered({ config, children }: { config: PortalConfig; children: React.ReactNode }) {
  return (
    <main className="page">
      <header className="centered-head">
        <h1>Watch Repair Pros</h1>
        <span className="muted">Staff portal · {config.version.slice(0, 7)}</span>
      </header>
      {children}
    </main>
  );
}
